/**
 * Calendar Process
 * 
 * Implements the Calendar layout: a single view containing a 7x5 grid (35 cells) for monthly view.
 * Each cell represents a day of the month.
 * 
 * **Layout Anatomy:**
 * - Single view with 7:5 aspect ratio (roughly)
 * - 7 columns (days of week: Sun, Mon, Tue, Wed, Thu, Fri, Sat)
 * - 5 rows (weeks of month)
 * - 35 day cells rendered internally using CSS Grid
 * - Each cell can display events/tasks for that day
 * 
 * **Characteristics:**
 * - Priority: NORMAL
 * - Lifecycle: STOP_ON_ALL_VIEWS_CLOSE
 * - Views: 1 view (contains all days internally)
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { 
  designTokens, 
  createHeading, 
  createButton,
  createContainer 
} from '../../core/design-system.js';
import { 
  renderCalendarCell, 
  renderCalendarDayNames,
  type CalendarCellConfig 
} from '../../core/calendar-cell-renderer.js';
import { renderNavigationButtons } from '../../core/navigation-buttons.js';
import { MONTH_NAMES } from '../../core/date-utils.js';
import { DataLoader } from '../../core/data-loader.js';
import type { CalendarEvent, CalendarDay, CalendarState } from '../../types/calendar.js';
import { 
  createCalendarEvent, 
  updateCalendarEvent, 
  deleteCalendarEvent as removeCalendarEvent,
  getEventsForDay 
} from '../../types/calendar.js';
import { CalendarEventStorage } from '../../services/storage/local-storage.js';
import { cortexBridge } from '../../services/cortex-bridge/service.js';

export class CalendarProcess extends BaseProcess {
  kind: string = 'calendar';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'productivity' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
    // No layoutMode - uses default grid allocation with 7:5 ratio preference
  };

  private state: CalendarState = {
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
    days: [],
    events: []
  };

  private calendarView: CalendarView | null = null;

  constructor(id: string) {
    super(id);
    // Load events from localStorage
    this.loadEvents();
    this.initializeDays();
    
    // Listen for remote updates
    bus.on('calendar.event.added.remote', (event: any) => {
      if (!this.state.events.some(e => e.id === event.id)) {
        this.state.events.push(event);
        CalendarEventStorage.add(event);
        this.initializeDays();
        this.updateState(this.state);
      }
    });
  }

  private loadEvents(): void {
    this.state.events = CalendarEventStorage.getAll();
  }

  private saveEvents(): void {
    CalendarEventStorage.save(this.state.events);
  }

  private initializeDays(): void {
    const { year, month } = this.state;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 6 = Saturday

    const days: CalendarDay[] = [];
    const today = new Date();

    // Fill in days from previous month (if needed)
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = prevMonthLastDay - i;
      days.push({
        date,
        month: month - 1,
        year: month === 0 ? year - 1 : year,
        isCurrentMonth: false,
        isToday: false,
        events: []
      });
    }

    // Fill in days of current month
    for (let date = 1; date <= daysInMonth; date++) {
      const isToday = today.getFullYear() === year &&
        today.getMonth() === month &&
        today.getDate() === date;
      const dayEvents = getEventsForDay(this.state.events, year, month, date);
      days.push({
        date,
        month,
        year,
        isCurrentMonth: true,
        isToday,
        events: dayEvents
      });
    }

    // Fill in days from next month (if needed to complete 35 cells)
    const remainingCells = 35 - days.length;
    for (let date = 1; date <= remainingCells; date++) {
      days.push({
        date,
        month: month + 1,
        year: month === 11 ? year + 1 : year,
        isCurrentMonth: false,
        isToday: false,
        events: []
      });
    }

    this.state.days = days;
  }

  protected onStart(ctx: ProcessContext): void {
    // Create a single calendar view containing all days
    const view = new CalendarView(
      this.id,
      `${this.id}-main`,
      this.state,
      this
    );
    const viewInstance = view.toViewInstance();
    this.calendarView = view;
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);

    // Emit process.started AFTER view is registered
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  updateState(newState: CalendarState): void {
    this.state = newState;
    this.saveEvents();
    // Update the single calendar view
    if (this.calendarView) {
      this.calendarView.updateState(newState);
    }
  }

  navigateToMonth(year: number, month: number): void {
    this.state.year = year;
    this.state.month = month;
    this.initializeDays();
    this.updateState(this.state);
    
    bus.emit('calendar.navigated', {
      processId: this.id,
      year,
      month
    });
  }

  /**
   * Add a new calendar event
   */
  addEvent(
    title: string,
    startTime: number,
    options: {
      event_type?: 'event' | 'task' | 'reminder' | 'meeting';
      description?: string;
      end_time?: number;
      priority?: 'low' | 'medium' | 'high';
      category?: string;
    } = {}
  ): void {
    const event = createCalendarEvent(title, startTime, options);
    this.state.events.push(event);
    CalendarEventStorage.add(event);
    this.initializeDays(); // Recalculate days with new events
    this.updateState(this.state);
    
    bus.emit('calendar.event.added', {
      processId: this.id,
      eventId: event.id,
      event
    });

    // Sync to Cortex
    cortexBridge.sendIntent('ADD_CALENDAR_EVENT', event);
  }

  /**
   * Update a calendar event
   */
  updateEvent(eventId: string, updates: Partial<CalendarEvent>): void {
    const index = this.state.events.findIndex(e => e.id === eventId);
    if (index !== -1) {
      this.state.events[index] = updateCalendarEvent(this.state.events[index], updates);
      CalendarEventStorage.update(eventId, this.state.events[index]);
      this.initializeDays(); // Recalculate days with updated events
      this.updateState(this.state);
      
      bus.emit('calendar.event.updated', {
        processId: this.id,
        eventId,
        event: this.state.events[index]
      });

      // Sync to Cortex
      cortexBridge.sendIntent('UPDATE_CALENDAR_EVENT', { id: eventId, updates });
    }
  }

  /**
   * Delete a calendar event
   */
  deleteEvent(eventId: string): void {
    this.state.events = removeCalendarEvent(this.state.events, eventId);
    CalendarEventStorage.delete(eventId);
    this.initializeDays(); // Recalculate days without deleted event
    this.updateState(this.state);
    
    bus.emit('calendar.event.deleted', {
      processId: this.id,
      eventId
    });

    // Sync to Cortex
    cortexBridge.sendIntent('DELETE_CALENDAR_EVENT', { id: eventId });
  }
}

/**
 * Calendar View (HTML-based)
 * 
 * Renders a complete calendar with all 35 days in a 7x5 grid.
 */
class CalendarView extends BaseViewHTML {
  constructor(
    processId: string,
    viewId: string,
    private state: CalendarState,
    private process: CalendarProcess
  ) {
    super(processId, viewId, {
      acceptableSizes: [
        { cols: 7, rows: 5 }, // Preferred: 7x5 (7:5 ratio)
        { cols: 6, rows: 4 },  // Alternative: 6x4 (1.5:1 ratio, close to 7:5)
        { cols: 8, rows: 6 },  // Alternative: 8x6 (4:3 ratio)
        { cols: 5, rows: 4 }   // Minimum: 5x4
      ],
      preferredRatio: 7 / 5, // 7:5 aspect ratio
      showDismissButton: true,
      resizable: true, // Calendar can be resized (will use toggle handle)
      priority: 100,
      // 0 = Half height, normal width
      // 1 = Full height, normal width
      // 2 = Half height, double width
      // 3 = Full height, double width
      sizes: [3, 0] // [3] = double+full (default), [0] = standard+half
    });
  }

  protected getContentHTML(): string {
    const currentMonthName = MONTH_NAMES[this.state.month];
    const currentYear = this.state.year;
    
    // Navigation buttons
    const header = renderNavigationButtons({
      onPrev: () => {
        const newMonth = this.state.month === 0 ? 11 : this.state.month - 1;
        const newYear = this.state.month === 0 ? this.state.year - 1 : this.state.year;
        this.process.navigateToMonth(newYear, newMonth);
      },
      onNext: () => {
        const newMonth = this.state.month === 11 ? 0 : this.state.month + 1;
        const newYear = this.state.month === 11 ? this.state.year + 1 : this.state.year;
        this.process.navigateToMonth(newYear, newMonth);
      },
      onToday: () => {
        const today = new Date();
        this.process.navigateToMonth(today.getFullYear(), today.getMonth());
      },
      showToday: true,
      centerContent: `${currentMonthName} ${currentYear}`
    });
    
    // Generate day cells using unified renderer
    const dayCells = this.state.days.map((day) => {
      const date = new Date(day.year, day.month, day.date);
      const events = day.events.map(event => ({
        id: event.id,
        title: event.title,
        priority: event.priority
      }));
      
      return renderCalendarCell({
        date,
        dayNumber: day.date,
        isCurrentMonth: day.isCurrentMonth,
        events,
        maxVisibleEvents: 3,
        onClick: day.isCurrentMonth ? () => {
          // Handle day click
      const dayKey = `${day.year}-${day.month}-${day.date}`;
          bus.emit('calendar.day.clicked', { dayKey, year: day.year, month: day.month, date: day.date });
        } : undefined,
        onEventClick: (eventId) => {
          bus.emit('calendar.event.clicked', { eventId });
        }
      });
    }).join('');

    return `
      <style>
        .calendar-container {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          padding: ${designTokens.padding.md};
          box-sizing: border-box;
        }
        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          grid-template-rows: repeat(5, 1fr);
          gap: ${designTokens.spacing.gridGap};
          flex: 1;
          min-height: 0;
        }
        .calendar-day:hover {
          background: rgba(255, 255, 255, 0.1) !important;
        }
        .event-bar:hover {
          opacity: 0.8;
        }
      </style>
      <div class="calendar-container">
        ${header}
        ${renderCalendarDayNames()}
        <div class="calendar-grid">
          ${dayCells}
        </div>
      </div>
    `;
  }

  protected render(): void {
    // Call parent to set base styles
    super.render();
    
    if (!this.element) return;
    
    // Setup navigation buttons
    const prevButton = this.element.querySelector('.calendar-nav-prev') as HTMLButtonElement;
    const nextButton = this.element.querySelector('.calendar-nav-next') as HTMLButtonElement;
    const todayButton = this.element.querySelector('.calendar-nav-today') as HTMLButtonElement;
    
    if (prevButton) {
      prevButton.addEventListener('click', () => {
        let newMonth = this.state.month - 1;
        let newYear = this.state.year;
        if (newMonth < 0) {
          newMonth = 11;
          newYear--;
        }
        this.process.navigateToMonth(newYear, newMonth);
      });
    }
    
    if (nextButton) {
      nextButton.addEventListener('click', () => {
        let newMonth = this.state.month + 1;
        let newYear = this.state.year;
        if (newMonth > 11) {
          newMonth = 0;
          newYear++;
        }
        this.process.navigateToMonth(newYear, newMonth);
      });
    }
    
    if (todayButton) {
      todayButton.addEventListener('click', () => {
        const today = new Date();
        this.process.navigateToMonth(today.getFullYear(), today.getMonth());
      });
    }
    
    // Setup day click handlers (add event)
    const dayCells = this.element.querySelectorAll('.calendar-day');
    dayCells.forEach(dayCell => {
      const isCurrentMonth = dayCell.getAttribute('data-day-key')?.includes(`-${this.state.month}-`);
      if (isCurrentMonth) {
        dayCell.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;
          // Don't trigger if clicking on an event
          if (target.classList.contains('calendar-event')) return;
          
          const year = parseInt(dayCell.getAttribute('data-year') || '0');
          const month = parseInt(dayCell.getAttribute('data-month') || '0');
          const date = parseInt(dayCell.getAttribute('data-date') || '0');
          
          const title = prompt('Event title:');
          if (title && title.trim()) {
            const startTime = new Date(year, month, date).setHours(12, 0, 0, 0);
            this.process.addEvent(title.trim(), startTime);
          }
        });
      }
    });
    
    // Setup event click handlers (view/edit/delete)
    const eventElements = this.element.querySelectorAll('.calendar-event');
    eventElements.forEach(eventEl => {
      eventEl.addEventListener('click', (e) => {
        e.stopPropagation();
        const eventId = eventEl.getAttribute('data-event-id');
        if (eventId) {
          const event = this.state.events.find(e => e.id === eventId);
          if (event) {
            const action = prompt(
              `Event: ${event.title}\n\nActions:\n1. Edit title\n2. Delete\n\nEnter 1 or 2:`
            );
            
            if (action === '1') {
              const newTitle = prompt('New title:', event.title);
              if (newTitle && newTitle.trim()) {
                this.process.updateEvent(eventId, { title: newTitle.trim() });
              }
            } else if (action === '2') {
              if (confirm(`Delete "${event.title}"?`)) {
                this.process.deleteEvent(eventId);
              }
            }
          }
        }
      });
    });
  }

  updateState(newState: CalendarState): void {
    log.debug(`[CalendarView] updateState() called for ${this.id}`);
    this.state = newState;
    this.markDirty();
  }
}

registerProcess('calendar', (id: string) => new CalendarProcess(id));



